import React, { useState } from "react";
import { roundedPanelSx } from "../../overviewPanel";
import {
  Box,
  Typography,
  Skeleton,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Modal,
  alpha,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PublicIcon from "@mui/icons-material/Public";
import { useIntrinsicValue, ScenarioType } from "../hooks/useIntrinsicValue";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatStockPrice } from "../../../../utils/stockPriceFormatter";

interface IntrinsicValueCardProps {
  ticker: string;
}

// Reusable header component with info modal
interface IntrinsicValueHeaderProps {
  onInfoClick: () => void;
  rightContent?: React.ReactNode;
}

const IntrinsicValueHeader: React.FC<IntrinsicValueHeaderProps> = ({
  onInfoClick,
  rightContent,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        mb: 2.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          sx={{
            color: theme.palette.text.primary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          Intrinsic Value
        </Typography>
        <IconButton
          size="small"
          onClick={onInfoClick}
          sx={{
            p: 0.25,
            color: theme.palette.text.secondary,
            "&:hover": {
              color: theme.palette.text.primary,
              backgroundColor: "transparent",
            },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>
      {rightContent}
    </Box>
  );
};

export const IntrinsicValueCard: React.FC<IntrinsicValueCardProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { currency } = useStockCurrency();
  const [selectedScenario, setSelectedScenario] =
    useState<ScenarioType>("baseCase");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const { data: scenariosData, isLoading, error } = useIntrinsicValue(ticker);

  const formatPrice = (value: number) => {
    return formatStockPrice({ price: value, currency });
  };

  const handleScenarioChange = (
    _: React.MouseEvent<HTMLElement>,
    newScenario: ScenarioType | null
  ) => {
    if (newScenario) {
      setSelectedScenario(newScenario);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        {/* Header with toggle skeleton */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
          }}
        >
          <Skeleton variant="text" width={140} height={20} />
          <Skeleton variant="rounded" width={200} height={32} />
        </Box>
        {/* Main value with badge skeleton */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
            <Skeleton variant="text" width={120} height={40} />
            <Skeleton variant="text" width={60} height={16} />
          </Box>
          <Skeleton variant="rounded" width={140} height={24} />
        </Box>
        {/* Bar visualization skeleton */}
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}
          >
            <Skeleton variant="rounded" sx={{ flex: 1 }} height={20} />
            <Skeleton variant="text" width={75} height={18} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Skeleton variant="rounded" sx={{ flex: 1 }} height={20} />
            <Skeleton variant="text" width={75} height={18} />
          </Box>
        </Box>
      </Box>
    );
  }

  // ADR with no coverage - show dedicated message
  const isAdrWithoutCoverage =
    scenariosData?.metadata.warning?.code === "adr_no_coverage";

  if (isAdrWithoutCoverage) {
    return (
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        <IntrinsicValueHeader onInfoClick={() => setIsInfoModalOpen(true)} />

        {/* Compact ADR Notice */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <PublicIcon
            sx={{
              fontSize: "1.25rem",
              color: theme.palette.info.main,
              mt: 0.25,
            }}
          />
          <Box>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              ADR - Limited Data
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.8rem",
                lineHeight: 1.5,
              }}
            >
              This ADR lacks reliable valuation data. Try searching for the
              primary listing (e.g. PUK → PRU.L).
            </Typography>
          </Box>
        </Box>

        {/* Info Modal */}
        <Modal
          open={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          aria-labelledby="adr-info-title"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", sm: "85%", md: 480 },
              maxWidth: 480,
              maxHeight: { xs: "85vh", sm: "80vh" },
              overflow: "auto",
              backgroundColor: theme.palette.background.surface,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: { xs: 2.5, sm: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Typography
                id="adr-info-title"
                sx={{
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                What is an ADR?
              </Typography>
              <IconButton
                size="small"
                onClick={() => setIsInfoModalOpen(false)}
                sx={{ p: 0.5, color: theme.palette.text.secondary }}
              >
                <CloseIcon sx={{ fontSize: "1.25rem" }} />
              </IconButton>
            </Box>

            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              An <strong>ADR (American Depositary Receipt)</strong> is a
              certificate that represents shares of a foreign company, allowing
              it to trade on US stock exchanges like the NYSE or NASDAQ.
            </Typography>

            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Why does this affect valuation?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              ADRs are secondary listings, so financial data providers often
              have incomplete or less reliable data compared to the
              company&apos;s primary listing on its home exchange.
            </Typography>

            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              How to find better data
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              Search for the company&apos;s original ticker symbol. Common
              exchange suffixes include:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, mb: 2 }}>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
              >
                <strong>.L</strong> — London Stock Exchange (UK)
              </Typography>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
              >
                <strong>.DE</strong> — Deutsche Börse (Germany)
              </Typography>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
              >
                <strong>.PA</strong> — Euronext Paris (France)
              </Typography>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                <strong>.TO</strong> — Toronto Stock Exchange (Canada)
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.03)"
                  : "rgba(0, 0, 0, 0.02)",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 0.5,
                p: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontStyle: "italic",
                  fontSize: "0.8rem",
                }}
              >
                Example: Instead of &quot;PUK&quot; (the US ADR), search for
                &quot;PRU.L&quot; to see Prudential plc on the London Stock
                Exchange.
              </Typography>
            </Box>
          </Box>
        </Modal>
      </Box>
    );
  }

  // Error or no data state
  if (error || !scenariosData) {
    return (
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        <IntrinsicValueHeader onInfoClick={() => setIsInfoModalOpen(true)} />

        {/* Compact Unavailable State */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <WarningAmberIcon
            sx={{
              fontSize: "1.25rem",
              color: theme.palette.warning.main,
              mt: 0.25,
            }}
          />
          <Box>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              Valuation Unavailable
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.8rem",
                lineHeight: 1.5,
              }}
            >
              Unable to calculate intrinsic value. Common reasons include:
              pre-revenue, unprofitable, negative cash flow, insufficient peer
              data, or limited financial history.
            </Typography>
          </Box>
        </Box>

        {/* Info Modal - same as main component */}
        <Modal
          open={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          aria-labelledby="dcf-methodology-title"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", sm: "85%", md: 680, lg: 720 },
              maxWidth: 720,
              maxHeight: { xs: "85vh", sm: "80vh" },
              overflow: "auto",
              backgroundColor: theme.palette.background.surface,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: { xs: 2.5, sm: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Typography
                id="dcf-methodology-title"
                sx={{
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                How We Calculate Intrinsic Value
              </Typography>
              <IconButton
                size="small"
                onClick={() => setIsInfoModalOpen(false)}
                sx={{ p: 0.5, color: theme.palette.text.secondary }}
              >
                <CloseIcon sx={{ fontSize: "1.25rem" }} />
              </IconButton>
            </Box>

            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              We blend <strong>DCF (Discounted Cash Flow)</strong> and{" "}
              <strong>Relative Valuation</strong> to estimate fair value, with
              weights varying by sector.
            </Typography>

            <Box component="ul" sx={{ pl: 2, m: 0, mb: 2 }}>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
              >
                <strong>DCF:</strong> Uses sector-appropriate models (growth
                DCF, EBITDA DCF, or dividend discount) to project intrinsic
                value
              </Typography>
              <Typography
                component="li"
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                <strong>Relative:</strong> Compares P/E and EV/EBITDA multiples
                against peer companies
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 2 }}
            >
              The three scenarios (Worst, Average, Best) reflect different
              assumptions — conservative, moderate, and optimistic — based on
              peer percentiles and valuation model ranges.
            </Typography>

            <Box
              sx={{
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.03)"
                  : "rgba(0, 0, 0, 0.02)",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 0.5,
                p: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontStyle: "italic",
                  fontSize: "0.8rem",
                }}
              >
                Note: Valuations are estimates based on models and assumptions.
                This is not financial advice.
              </Typography>
            </Box>
          </Box>
        </Modal>
      </Box>
    );
  }

  const { stockPrice, scenarios } = scenariosData;
  const currentScenario = scenarios[selectedScenario];
  const dcf = currentScenario.intrinsicValue;

  // Calculate valuation metrics
  // Upside/downside = how much the stock price needs to move to reach fair value
  const percentDiff = ((dcf - stockPrice) / stockPrice) * 100;
  const isUndervalued = stockPrice < dcf;
  const upsideDownside = Math.abs(percentDiff);

  return (
    <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
      {/* Header with Scenario Toggle */}
      <IntrinsicValueHeader
        onInfoClick={() => setIsInfoModalOpen(true)}
        rightContent={
          <ToggleButtonGroup
            value={selectedScenario}
            exclusive
            onChange={handleScenarioChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontSize: "0.7rem",
                fontWeight: 500,
                px: 1.25,
                py: 0.375,
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                },
                "&.Mui-selected": {
                  backgroundColor: "transparent",
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                },
              },
            }}
          >
            <ToggleButton value="worstCase">Worst</ToggleButton>
            <ToggleButton value="baseCase">Average</ToggleButton>
            <ToggleButton value="bestCase">Best</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {/* Main Value Display - Fair Value */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 2 }}>
        <Typography
          sx={{
            fontSize: "2.25rem",
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          {formatPrice(dcf)}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.02em",
          }}
        >
          Fair Value
        </Typography>
      </Box>

      {/* Bar Visualization with Integrated Values */}
      {(() => {
        // Both bars use the same scale from 0 to max(intrinsicValue, price)
        // This makes it easy to visually compare the two values
        const maxValue = Math.max(stockPrice, dcf);

        // Calculate percentages relative to the max value
        const dcfPercent = (dcf / maxValue) * 100;
        const pricePercent = (stockPrice / maxValue) * 100;

        // Theme-aware colors for bars using theme palette
        const barBackground = isDark
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.05)";
        const neutralFill = alpha(
          theme.palette.secondary.main,
          isDark ? 0.5 : 0.6
        );
        // Use higher opacity to match Analyst Grades chart vibrant colors
        const positiveFill = alpha(
          theme.palette.success.main,
          isDark ? 0.8 : 0.85
        );
        const negativeFill = alpha(
          theme.palette.error.main,
          isDark ? 0.8 : 0.85
        );

        return (
          <Box>
            {/* Dual Horizontal Bars */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {/* Intrinsic Value Bar */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 20,
                    backgroundColor: barBackground,
                    borderRadius: 0.5,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      width: `${dcfPercent}%`,
                      // Intrinsic Value bar is always colored: green when undervalued, red when overvalued
                      backgroundColor: isUndervalued
                        ? positiveFill
                        : negativeFill,
                      borderRadius: 0.5,
                      transition: "width 0.3s ease-out",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    // Intrinsic Value text matches bar color
                    color: isUndervalued
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                    minWidth: 75,
                    textAlign: "right",
                  }}
                >
                  {formatPrice(dcf)}
                </Typography>
              </Box>

              {/* Price Bar */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 20,
                    backgroundColor: barBackground,
                    borderRadius: 0.5,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      width: `${pricePercent}%`,
                      // Price bar is always neutral gray
                      backgroundColor: neutralFill,
                      borderRadius: 0.5,
                      transition: "width 0.3s ease-out",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    // Price text is always neutral
                    color: theme.palette.text.secondary,
                    minWidth: 75,
                    textAlign: "right",
                  }}
                >
                  {formatPrice(stockPrice)}
                </Typography>
              </Box>
            </Box>

            {/* Bar Labels */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0.5,
                pr: "87px", // Align with bar values (75px + 12px gap)
              }}
            >
              {/* Valuation indicator */}
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  color: isUndervalued
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  textTransform: "uppercase",
                  letterSpacing: "0.02em",
                }}
              >
                {isUndervalued ? "UNDERVALUED" : "OVERVALUED"}{" "}
                {upsideDownside.toFixed(0)}%
              </Typography>

              {/* Bar legend */}
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: isUndervalued
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                  }}
                >
                  Fair Value
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: theme.palette.text.secondary,
                    opacity: 0.3,
                  }}
                >
                  |
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: theme.palette.text.secondary,
                    opacity: 0.6,
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                  }}
                >
                  Current Price
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}

      {/* Methodology Info Modal */}
      <Modal
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        aria-labelledby="dcf-methodology-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: "85%", md: 680, lg: 720 },
            maxWidth: 720,
            maxHeight: { xs: "85vh", sm: "80vh" },
            overflow: "auto",
            backgroundColor: theme.palette.background.surface,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: { xs: 2.5, sm: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Typography
              id="dcf-methodology-title"
              sx={{
                fontSize: "1.125rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              How We Calculate Intrinsic Value
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsInfoModalOpen(false)}
              sx={{ p: 0.5, color: theme.palette.text.secondary }}
            >
              <CloseIcon sx={{ fontSize: "1.25rem" }} />
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            We blend <strong>DCF (Discounted Cash Flow)</strong> and{" "}
            <strong>Relative Valuation</strong> to estimate fair value, with
            weights varying by sector.
          </Typography>

          <Box component="ul" sx={{ pl: 2, m: 0, mb: 2 }}>
            <Typography
              component="li"
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
            >
              <strong>DCF:</strong> Uses sector-appropriate models (growth DCF,
              EBITDA DCF, or dividend discount) to project intrinsic value
            </Typography>
            <Typography
              component="li"
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              <strong>Relative:</strong> Compares P/E and EV/EBITDA multiples
              against peer companies
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            The three scenarios (Worst, Average, Best) reflect different
            assumptions — conservative, moderate, and optimistic — based on peer
            percentiles and valuation model ranges.
          </Typography>

          <Box
            sx={{
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.03)"
                : "rgba(0, 0, 0, 0.02)",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0.5,
              p: 1.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontStyle: "italic",
                fontSize: "0.8rem",
              }}
            >
              Note: Valuations are estimates based on models and assumptions.
              This is not financial advice.
            </Typography>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default IntrinsicValueCard;
